import { Map } from 'immutable'
import { createSelector } from 'reselect'
import { identity } from 'lodash'
import * as constants from './constants'
import * as actions from './actions'

const DEFAULT_DOMAINS = ['fwd-dev.drifttmail.com']

export const root = state => state.get('inbox')

export const inbox = inboxId => createSelector(root, root => root.getIn(['byId', inboxId], Map()))
export const currentInboxId = createSelector(root, root => root.get('currentInboxId'))
export const inboxes = createSelector(root, root => root.get('byId', Map()))

export const countsById = createSelector(root, root => root.get('countsById'))
export const countsById2 = createSelector(root, root => root.get('countsById2'))
export const countsStatusById = createSelector(root, root => root.get('countsStatusById'))

export const makeCountsSelector = () =>
  createSelector([countsById, (state, props) => props.inboxId], (countsById, inboxId) => {
    return countsById.get(inboxId, Map())
  })

// TODO: Prioritize count of loaded, fully paged conversations over counts in
// the store when counts from the store are larger than the paged convo count.
export const makeCountsSelector2 = () =>
  createSelector([countsById2, (state, props) => props.inboxId], (countsById2, inboxId) => {
    return countsById2.get(inboxId, Map())
  })

export const makeIsCountStatusPending = () =>
  createSelector([countsStatusById, (state, props) => props.inboxId], (countsStatusById, inboxId) => {
    return countsStatusById.get(inboxId) === actions.fetchCountsPending.toString()
  })

export const makeIsAnyCountStatusPending = () =>
  createSelector([countsStatusById, (state, props) => props.inboxIds], (countsStatusById, inboxIds) => {
    return inboxIds
      .map(inboxId => {
        return countsStatusById.get(inboxId) === actions.fetchCountsPending.toString()
      })
      .some(isCountStatusPending => isCountStatusPending)
  })

export const makeIsCountStatusLoaded = () =>
  createSelector([countsStatusById, (state, props) => props.inboxId], (countsStatusById, inboxId) => {
    return [
      actions.receiveCounts.toString(),
      actions.receiveCounts2.toString(),
    ].includes(countsStatusById.get(inboxId))
  })

export const inboxOptions = createSelector(
  inboxes,
  inboxes =>
    !!inboxes &&
    inboxes.valueSeq().map(inbox =>
      Map({
        value: inbox.id,
        label: inbox.name ? `${inbox.name} (${inbox.address})` : inbox.address,
      })
    )
)

export const inboxOptionsWithLabels = createSelector(
  inboxes,
  inboxes =>
    !!inboxes &&
    inboxes.valueSeq().map(inbox =>
      Map({
        value: inbox.id,
        label: `${inbox.name} (${inbox.address})`,
      })
    )
)

export const createdInboxId = createSelector(root, root => {
  return !!root.get('createdInboxId') && root.get('createdInboxId')
})

export const isInboxListLoaded = createSelector(root, root => {
  const status = root.get('status')
  return status === constants.RECEIVE_INBOX_LIST
})
export const isInboxListLoading = createSelector(root, root => {
  const status = root.get('status')
  return status === constants.FETCH_INBOX_LIST || status === constants.FETCH_INBOX_LIST_PENDING
})

export const isSendingDomainLoaded = createSelector(root, root => {
  const status = root.get('status')
  return status === constants.RECEIVE_INBOX_LIST
})
export const isSendingDomainLoading = createSelector(root, root => {
  const status = root.get('status')
  return status === constants.FETCH_SENDING_DOMAINS || status === constants.FETCH_COUNTS_PENDING
})

export const isInboxLoading = (state, inboxId) =>
  [constants.FETCH_INBOX, constants.FETCH_INBOX_PENDING].includes(
    root(state).getIn(['byId', inboxId, 'fetchStatus'])
  )
export const isInboxLoaded = (state, inboxId) =>
  root(state).getIn(['byId', inboxId, 'fetchStatus']) === constants.RECEIVE_INBOX

export const nonMissingWhitelabels = state =>
  root(state)
    .get('byDomain', Map())
    .filter(
      domain =>
        !domain.getIn(['data', 'missing']) &&
        !domain.getIn(['data', 'fetchStatus']) !== constants.FETCH_WHITELABEL_PENDING
    )
    .map(record => record.get('data'))
export const nonDefaultNonMissingWhitelabels = state =>
  nonMissingWhitelabels(state).filter((record, key) => !DEFAULT_DOMAINS.includes(key))
export const isWhitelabelLoading = (state, domain) =>
  [constants.FETCH_WHITELABEL, constants.FETCH_WHITELABEL_PENDING].includes(
    root(state).getIn(['byDomain', domain, 'data', 'fetchStatus'])
  )
export const isWhitelabelLoaded = (state, domain) =>
  root(state).getIn(['byDomain', domain, 'data', 'fetchStatus']) === constants.FETCH_WHITELABEL_SUCCESS
export const isAnyWhiteLabelLoading = state =>
  root(state)
    .get('byDomain', Map())
    .some(domain =>
      [constants.FETCH_WHITELABEL, constants.FETCH_WHITELABEL_PENDING].includes(
        domain.getIn(['data', 'fetchStatus'])
      )
    )
export const isAnyWhiteLabelLoaded = state =>
  root(state)
    .get('byDomain', Map())
    .some(domain =>
      [constants.FETCH_WHITELABEL_FAILED, constants.FETCH_WHITELABEL_SUCCESS].includes(
        domain.getIn(['data', 'fetchStatus'])
      )
    )
export const isWhitelabelOpen = (state, domain) =>
  root(state).getIn(['byDomain', domain, 'ui', 'isAccordionOpen'], false)

export const whitelabel = (state, domain) => root(state).getIn(['byDomain', domain, 'data'])
export const isWhitelabelVerified = (state, domain) => {
  const validities = root(state)
    .getIn(['byDomain', domain, 'data', 'dns'], Map())
    .map(record => record.get('valid'))
  return validities.size !== 0 && validities.every(identity)
}

export const sendingDomains = state => root(state).getIn(['sendingDomains', 'data', 'byId'], Map())

export const isSendingDomainsLoading = createSelector(root, root => {
  const status = root.getIn(['sendingDomains', 'status', 'fetch'], Map())
  return status === constants.FETCH_SENDING_DOMAINS || status === constants.FETCH_SENDING_DOMAINS_PENDING
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/inbox/selectors.js